import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { navigate, useRouteQuery } from "../lib/router.js";
import { Button } from "../components/Button.js";
import { Field } from "../components/Field.js";
import { BlazeMark } from "../components/Panel.js";

/**
 * Installed-PWA sign-in fallback (handoff §7 "Installed PWA authentication"):
 * the email browser and the installed PWA do not share a session, so the user
 * pastes the full sign-in link into the installed app and redeems its token
 * here. We parse only the expected origin/path/token shape and reject
 * anything else. This stays magic-link auth — no OTP/PIN system.
 */
function extractTokenFromLink(raw: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(raw.trim());
  } catch {
    return null;
  }
  // Only accept our own origin and the exact confirm path.
  if (parsed.origin !== window.location.origin) return null;
  if (!parsed.hash.startsWith("#/confirm")) return null;
  const qIdx = parsed.hash.indexOf("?");
  const query = new URLSearchParams(qIdx >= 0 ? parsed.hash.slice(qIdx + 1) : "");
  const token = query.get("token");
  return token && token.length >= 16 ? token : null;
}

export function CheckEmailScreen() {
  const query = useRouteQuery();
  const email = query.get("email") ?? "";
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(60);
  const [busy, setBusy] = useState(false);
  const [paste, setPaste] = useState("");
  const [pasting, setPasting] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function resend() {
    if (cooldown > 0) return;
    setError(null);
    setBusy(true);
    try {
      await api.requestMagicLink(email);
      setCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function redeemPasted() {
    const token = extractTokenFromLink(paste);
    if (!token) {
      setError("That doesn't look like a valid lingoLoco sign-in link.");
      return;
    }
    setError(null);
    setPasting(true);
    try {
      await api.redeem(token, crypto.randomUUID());
      window.location.assign("/");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "This link is no longer valid.";
      setError(
        /expired|consumed|no longer/i.test(msg)
          ? "This link has already been used. Request a fresh link."
          : msg,
      );
    } finally {
      setPasting(false);
    }
  }

  return (
    <div className="min-h-dvh shell-surface px-[var(--spacing-gutter)] py-8 max-w-md mx-auto flex flex-col justify-center">
      <BlazeMark solid className="mb-4" />
      <h1 className="font-display text-[28px] text-granite-900 dark:text-granite-50 mb-2">
        Check your email
      </h1>
      <p className="font-text text-granite-600 dark:text-granite-300 mb-6 leading-relaxed">
        We sent a sign-in link to <strong className="text-granite-800 dark:text-granite-100">{email}</strong>.
        Open it on this phone to continue. The link expires in 15 minutes.
      </p>

      <div className="panel p-4 mb-6">
        <h2 className="font-text font-semibold text-[17px] mb-2 text-granite-800 dark:text-granite-100">
          On an installed app?
        </h2>
        <p className="font-text text-[15px] text-granite-600 dark:text-granite-300 mb-3">
          If the email opens in a browser but you want to sign in inside the app,
          copy the full link from the email and paste it here, then tap Use link.
        </p>
        <Field
          label="Paste sign-in link"
          hint="Paste the whole link from the email, including the token."
          placeholder="https://lingoloco.mandorum.com/#/confirm?token=…"
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          inputMode="url"
          autoComplete="off"
        />
        <div className="mt-3">
          <Button onClick={redeemPasted} disabled={pasting || !paste.trim()}>
            {pasting ? "Signing in…" : "Use link"}
          </Button>
        </div>
      </div>

      {error && <p className="font-text text-blaze text-[15px] mb-4" role="alert">{error}</p>}

      <div className="flex flex-col gap-3">
        <Button onClick={resend} variant="secondary" disabled={busy || cooldown > 0}>
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend link"}
        </Button>
        <Button variant="ghost" onClick={() => navigate("/")}>
          Back
        </Button>
      </div>
    </div>
  );
}
